import { Controller } from "@hotwired/stimulus"
import { nextRead } from "../lib/arxiv_status_schedule"

// Connects to data-controller="arxiv-status"
//
// Reads the status frame again while a paper in it is still being generated.
// The controller sits on the frame, not inside it, because Turbo replaces a
// frame's children and leaves its attributes alone: what is counted between
// reads has to outlive the answer that arrives.
//
// A frame holding only finished papers is never read again, so a tab left open
// on a result asks for nothing.
export default class extends Controller {
  static targets = ["generating", "manualCheck"]

  connect() {
    this.reset()
    this.watchVisibility = () => this.visibilityChanged()
    document.addEventListener("visibilitychange", this.watchVisibility)
    // The frame can answer before this controller connects, and its load event
    // is gone by then.
    this.loaded()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.watchVisibility)
    this.cancel()
  }

  // Every answer decides whether there is another one.
  loaded() {
    this.cancel()
    if (!this.hasGeneratingTarget) return this.reset()

    this.since ??= Date.now()
    const next = nextRead(this.watched)
    if (next.action === "stop") return this.giveUp()

    this.timer = window.setTimeout(() => this.element.reload(), next.delay)
  }

  // A read that never arrived ends the sequence as surely as a finished paper.
  // Only this frame's own reads count: Send to Kindle and the OCR payment post
  // from inside it, and their failures are answered on their own pages.
  readFailed(event) {
    if (event.target !== this.element) return

    this.giveUp()
  }

  // The way back: one read, and the count starts over.
  checkNow() {
    this.reset()
    this.element.reload()
  }

  // Time a hidden tab spends away is not time the visitor waited, so it must not
  // spend the limit. A conversion usually finishes while the tab is away — read
  // once the moment it comes back.
  visibilityChanged() {
    if (document.hidden) return this.pause()
    if (this.hasGeneratingTarget) this.element.reload()
  }

  pause() {
    this.cancel()
    if (this.since === null) return

    this.watchedBefore += Date.now() - this.since
    this.since = null
  }

  // Stop, and hand the visitor the button rather than leave them on a spinner.
  giveUp() {
    this.cancel()
    if (this.hasManualCheckTarget) this.manualCheckTarget.removeAttribute("hidden")
  }

  cancel() {
    window.clearTimeout(this.timer)
    this.timer = null
  }

  reset() {
    this.watchedBefore = 0
    this.since = null
  }

  get watched() {
    return this.watchedBefore + (this.since === null ? 0 : Date.now() - this.since)
  }
}
